import React, { useMemo, useLayoutEffect, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import {
  META_PIXEL_ID,
  loadMetaPixel,
  trackPurchase as metaPurchase
} from '../../../../../../../utils/metaPixel';

import {
  loadTikTokPixel,
  trackPurchase as ttPurchase
} from '../../../../../../../utils/tiktokPixel';

type PackItem = {
  id: string;
  title: string;
  price: string;
  entries: number;
  nfts: number;
  tokens?: number;
  description: string;
  dotColor: string;
  bonus?: {
    description: string;
    extraCards: number;
    extraEntries: number;
    price: number;
  };
};

type EntryFormValues = {
  fullName?: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
};

export type GiveawayConfirmationState = {
  entry: EntryFormValues;
  selectedPack: PackItem;

  // numbers shown in the “Entry Details” card
  totalEntries: number;
  nftRewards: number;
  amountPaidAud: number;

  // ✅ NEW
  checkoutMode?: 'standalone' | 'subscription';
  incPerMonth?: number; // or tokensPerMonth, whatever you prefer

  isExistingVerifiedUser?: boolean;

  memberPortalPath?: string;
  discordInviteUrl?: string;

  upsellApplied?: any;
  bonusSelected?: boolean;
  paymentIntentId?: string;
  subscriptionId?: string;
};

const cardShellSx = {
  borderRadius: 4,
  border: '1px solid rgba(18, 31, 38, 0.10)',
  background: '#ffffff',
  boxShadow: '0 24px 64px rgba(18, 31, 38, 0.10)',
  overflow: 'hidden'
};

/* A label and its value, side by side. The card clips (cardShellSx), so on a
 * phone a pair wider than the card lost its end: a 34-character email — not an
 * unusual length — was cut off mid-address on every phone narrower than about
 * 425px, and the long labels were squeezed into narrow columns beside their
 * chips. The row may now wrap: a pair that does not fit stacks, the value (each
 * row's last child) keeping to the right and the label reading from the left,
 * and an unbroken address breaks where it must. A pair that fits stays on one
 * line as before. The same fix as the entry-details rows on
 * /giveaway-offer/subscribed. */
const rowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  columnGap: 1,
  rowGap: 0.5,
  py: 1.6,
  '& > :last-child': {
    ml: 'auto',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
    textAlign: 'right'
  }
} as const;

const labelSx = {
  color: 'rgba(17,24,39,0.58)',
  fontWeight: 700,
  textAlign: 'left'
} as const;
const valueSx = { color: '#111827', fontWeight: 900 };

const RafflePaymentConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID || '';
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    // once you’re on confirmation, blow away “flow” state
    sessionStorage.removeItem('rafflePaymentPayload');
    sessionStorage.removeItem('giveawayUpsellPaymentIntentId');
    sessionStorage.removeItem('giveawayUpsellApplied');
    // optional: if you don’t want confirmation to survive refresh either
    // sessionStorage.removeItem('giveawayConfirmationState');
  }, []);

  useEffect(() => {
    const onPopState = () => {
      // Send them to giveaway instead of returning to payment
      navigate('/giveaway', { replace: true });
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigate]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  }, []);

  const state = useMemo<GiveawayConfirmationState | null>(() => {
    const fromState = location.state as GiveawayConfirmationState | undefined;
    if (fromState) return fromState;

    const saved = sessionStorage.getItem('giveawayConfirmationState');
    return saved ? (JSON.parse(saved) as GiveawayConfirmationState) : null;
  }, [location.state]);

  useEffect(() => {
    if (!state) return;

    const uniquePurchaseId = state.paymentIntentId || state.subscriptionId;

    if (!uniquePurchaseId) return;

    const purchaseKey = `purchase_tracked:${uniquePurchaseId}`;

    // prevent double firing
    if (sessionStorage.getItem(purchaseKey)) return;

    const contentId = String(state.selectedPack?.id ?? '');
    const contentName = state.selectedPack?.title ?? 'Unknown Pack';
    const valueAud = Number(state.amountPaidAud ?? 0);

    if (!contentId || !Number.isFinite(valueAud) || valueAud <= 0) return;

    const upsellPurchased = Boolean(state.upsellApplied);

    // mark as tracked
    sessionStorage.setItem(purchaseKey, '1');

    if (META_PIXEL_ID) {
      loadMetaPixel(META_PIXEL_ID);

      metaPurchase({
        value: valueAud,
        currency: 'AUD',
        contentId,
        contentName,
        upsellPurchased,
        quantity: 1
      });
    }

    if (TIKTOK_PIXEL_ID) {
      loadTikTokPixel(TIKTOK_PIXEL_ID);

      ttPurchase({
        value: valueAud,
        currency: 'AUD',
        contentId,
        contentName,
        upsellPurchased,
        quantity: 1
      });
    }
  }, [state, TIKTOK_PIXEL_ID]);

  // if someone hard-refreshes without state, bounce them somewhere safe
  if (!state) {
    return (
      <Box sx={{ px: { xs: 2, md: 4, lg: 8 }, py: 6 }}>
        <Paper sx={{ ...cardShellSx, p: { xs: 2.5, md: 3.5 } }}>
          <Typography sx={{ ...valueSx, fontSize: 22 }}>
            We couldn’t find your confirmation details.
          </Typography>
          <Typography
            sx={{ mt: 1, color: 'rgba(17,24,39,0.62)', fontWeight: 700 }}
          >
            Please head back to the giveaway page.
          </Typography>

          <Box sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              sx={{
                borderRadius: 999,
                fontWeight: 950,
                background:
                  'linear-gradient(90deg, rgba(124,255,250,0.35) 0%, rgba(124,255,250,0.95) 100%)',
                color: 'rgba(0,0,0,0.85)'
              }}
              onClick={() => navigate('/giveaway')}
            >
              Back to Giveaway
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const {
    entry,
    selectedPack,
    totalEntries,
    nftRewards,
    amountPaidAud,
    isExistingVerifiedUser = false,
    memberPortalPath = '/login'
  } = state;

  console.log('state', state);

  const discordInviteUrl = 'https://discord.gg/xvga3TZK7D';

  const isSubscription = state.checkoutMode === 'subscription';

  const entriesLabel = isSubscription
    ? 'Total Entries / Month'
    : 'Total Entries';
  const nftsLabel = isSubscription ? 'Digital Collectible Rewards / Month' : 'Digital Collectible Rewards';
  const paidLabel = isSubscription ? 'Amount Paid / Month' : 'Amount Paid';

  const fullName =
    `${entry?.firstName ?? ''} ${entry?.lastName ?? ''}`.trim() ||
    entry?.username ||
    '—';

  return (
    <Box
      sx={{
        width: '100%',
        py: 6,
        minHeight: '100vh',
        backgroundColor: '#f8fafc'
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', md: '100%', lg: '50%' },
          maxWidth: { xs: '100%', md: '100%', lg: 860 },
          mx: 'auto',
          px: { xs: 2, sm: 2.5, md: 3 }
        }}
      >
        {/* HERO HEADER */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 4.5,
            pt: { xs: 1, md: 2 },
            maxWidth: 980,
            mx: 'auto'
          }}
        >
          {!isExistingVerifiedUser && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 0.8,
                borderRadius: 999,
                mb: 2,
                border: '1px solid rgba(36, 191, 189, 0.25)',
                background: 'rgba(36, 191, 189, 0.10)'
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 950,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#0891b2'
                }}
              >
                Welcome to Incrypt
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: { xs: 34, sm: 44, md: 56 },
              fontWeight: 1000,
              letterSpacing: '-0.035em',
              lineHeight: 1.05,
              color: '#111827'
            }}
          >
            Congratulations
          </Typography>

          <Typography
            sx={{
              mt: 1.1,
              fontSize: { xs: 20, sm: 22, md: 26 },
              fontWeight: 900,
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
              // Premium gradient text (subtle)
              background:
                'linear-gradient(90deg, #24bfbd 0%, #111827 55%, #24bfbd 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            You&apos;re officially in the draw
          </Typography>

          {/* soft divider glow */}
          <Box
            sx={{
              mt: 2.5,
              mx: 'auto',
              width: { xs: 140, md: 180 },
              height: 2,
              borderRadius: 999,
              background:
                'linear-gradient(90deg, rgba(36,191,189,0) 0%, rgba(36,191,189,0.75) 50%, rgba(36,191,189,0) 100%)',
              boxShadow: '0 0 18px rgba(36,191,189,0.12)'
            }}
          />
        </Box>

        {/* ENTRY DETAILS */}
        <Paper sx={{ ...cardShellSx, mb: 3, textAlign: 'center' }}>
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 950,
                color: '#111827'
              }}
            >
              Entry Details
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(18,31,38,0.08)' }} />

          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 1 }}>
            <Box sx={rowSx}>
              <Typography sx={labelSx}>Email</Typography>
              <Typography sx={{ ...valueSx, fontWeight: 800 }}>
                {entry?.email ?? '—'}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: 'rgba(18,31,38,0.08)' }} />

            <Box sx={rowSx}>
              <Typography sx={labelSx}>{entriesLabel}</Typography>
              <Chip
                label={`${totalEntries} Entries`}
                sx={{
                  fontWeight: 950,
                  color: '#111827',
                  background: '#f8fafc',
                  border: '1px solid rgba(18,31,38,0.10)'
                }}
              />
            </Box>
            <Divider sx={{ borderColor: 'rgba(18,31,38,0.08)' }} />

            <Box sx={rowSx}>
              <Typography sx={labelSx}>{nftsLabel}</Typography>
              <Chip
                label={`${nftRewards} Digital Collectibles`}
                sx={{
                  fontWeight: 950,
                  color: '#111827',
                  background: '#f8fafc',
                  border: '1px solid rgba(18,31,38,0.10)'
                }}
              />
            </Box>
            <Divider sx={{ borderColor: 'rgba(18,31,38,0.08)' }} />

            {isSubscription && (
              <>
                <Box sx={rowSx}>
                  <Typography sx={labelSx}>INC Earned / Month</Typography>
                  <Chip
                    label={`A$${Number(state.incPerMonth ?? 0)} of INC`}
                    sx={{
                      fontWeight: 950,
                      color: '#111827',
                      background: '#f8fafc',
                      border: '1px solid rgba(18,31,38,0.10)'
                    }}
                  />
                </Box>
                <Divider sx={{ borderColor: 'rgba(18,31,38,0.08)' }} />
              </>
            )}

            <Box sx={rowSx}>
              <Typography sx={labelSx}>{paidLabel}</Typography>
              <Typography sx={{ ...valueSx, fontSize: 22 }}>
                ${Number(amountPaidAud || 0).toFixed(2)}
              </Typography>
            </Box>

            {/* <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} /> */}

            {/* <Box sx={{ ...rowSx, pb: 2 }}>
              <Typography sx={labelSx}>Status</Typography>
              <Chip
                label="Completed"
                sx={{
                  fontWeight: 950,
                  color: 'rgba(0,0,0,0.80)',
                  background: 'rgba(118, 255, 144, 0.90)'
                }}
              />
            </Box> */}
          </Box>
        </Paper>

        {/* MANAGE ACCOUNT */}
        <Paper
          sx={{
            ...cardShellSx,
            mb: 3,
            p: { xs: 2.5, md: 3.5 },
            textAlign: 'center'
          }}
        >
          {!isExistingVerifiedUser && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.2,
                color: '#475569',
                background: '#f8fafc',
                border: '1px solid rgba(18,31,38,0.08)',
                borderRadius: 999,
                px: 1.5,
                py: 0.9
              }}
            >
              <EmailOutlinedIcon sx={{ fontSize: 18, color: '#0891b2' }} />
              <Typography
                sx={{ fontWeight: 800, fontSize: 13, color: '#475569' }}
              >
                Your login details have been emailed to you – check your inbox!
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: { xs: 20, md: 28 },
              fontWeight: 950,
              color: '#111827'
            }}
          >
            Manage Your Account &amp; View Your Entries
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontWeight: 700,
              color: 'rgba(17,24,39,0.62)',
              maxWidth: 760
            }}
          >
            Access the member portal to view your entries, track your purchases,
            and manage your account.
          </Typography>

          <Box sx={{ mt: 2.5 }}>
            <Button
              onClick={() => navigate(memberPortalPath)}
              startIcon={<LaunchIcon />}
              sx={{
                height: 46,
                px: 3,
                borderRadius: 999,
                fontWeight: 950,
                color: 'rgba(0,0,0,0.85)',
                background:
                  'linear-gradient(90deg, rgba(124,255,250,0.35) 0%, rgba(124,255,250,0.95) 100%)',
                border: '1px solid rgba(124,255,250,0.25)'
              }}
            >
              Go to Member Portal
            </Button>
          </Box>
        </Paper>

        {/* DISCORD */}
        <Paper
          sx={{
            ...cardShellSx,
            mb: 4,
            p: { xs: 2.5, md: 3.5 },
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 28 },
              fontWeight: 950,
              color: '#111827'
            }}
          >
            Join Our Discord Community
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontWeight: 700,
              color: 'rgba(17,24,39,0.62)',
              maxWidth: 760
            }}
          >
            Join our Discord to watch our live draws every Friday at 10pm AEST.
          </Typography>

          <Box sx={{ mt: 2.5 }}>
            <Button
              component="a"
              href={discordInviteUrl}
              target="_blank"
              rel="noreferrer"
              startIcon={<LaunchIcon />}
              sx={{
                height: 46,
                px: 3,
                borderRadius: 999,
                fontWeight: 950,
                color: '#ffffff',
                background: '#5865f2',
                border: '1px solid rgba(88, 101, 242, 0.30)',
                '&:hover': { background: '#4653da' }
              }}
            >
              Join Discord Server
            </Button>
          </Box>
        </Paper>

        {/* WHAT HAPPENS NEXT */}
        <Typography
          sx={{
            textAlign: 'center',
            fontWeight: 950,
            color: '#111827',
            mb: 2,
            fontSize: 24
          }}
        >
          What Happens Next?
        </Typography>

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Paper sx={{ ...cardShellSx, p: 2.5 }}>
              <EmailOutlinedIcon sx={{ color: '#24bfbd' }} />
              <Typography
                sx={{
                  mt: 1.2,
                  fontWeight: 950,
                  color: '#111827'
                }}
              >
                Check Your Email
              </Typography>
              <Typography
                sx={{
                  mt: 0.8,
                  fontWeight: 700,
                  color: 'rgba(17,24,39,0.62)',
                  fontSize: 15
                }}
              >
                We’ve sent a confirmation email with all your entry details.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Paper sx={{ ...cardShellSx, p: 2.5 }}>
              <CalendarMonthOutlinedIcon sx={{ color: '#24bfbd' }} />
              <Typography
                sx={{
                  mt: 1.2,
                  fontWeight: 950,
                  color: '#111827'
                }}
              >
                Mark Your Calendar
              </Typography>
              <Typography
                sx={{
                  mt: 0.8,
                  fontWeight: 700,
                  color: 'rgba(17,24,39,0.62)',
                  fontSize: 15
                }}
              >
                The draw date is announced on our website and Discord.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Paper sx={{ ...cardShellSx, p: 2.5 }}>
              <EmojiEventsOutlinedIcon sx={{ color: '#24bfbd' }} />
              <Typography
                sx={{
                  mt: 1.2,
                  fontWeight: 950,
                  color: '#111827'
                }}
              >
                Winner Announced
              </Typography>
              <Typography
                sx={{
                  mt: 0.8,
                  fontWeight: 700,
                  color: 'rgba(17,24,39,0.62)',
                  fontSize: 15
                }}
              >
                Winners will be notified via email and announced publicly.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              borderColor: 'rgba(18,31,38,0.14)',
              color: '#111827'
            }}
            onClick={() => navigate('/giveaway')}
          >
            Back to Giveaway
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default RafflePaymentConfirmationPage;
